import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import {
  FiX,
  FiSave,
  FiSend,
  FiZap,
  FiClock,
  FiXCircle,
  FiInfo,
  FiAlertTriangle,
  FiUsers,
  FiFileText,
  FiChevronDown,
  FiTrash2,
} from "react-icons/fi";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { eyebrow, mono } from "../styles";
import TemplateDropzone from "./TemplateDropzone";
import TemplatePreviewFrame from "./TemplatePreviewFrame";
import ConfirmDialog from "../../../components/ConfirmDialog";

const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const modalIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 720px;
  height: min(860px, 94vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${tokens.shadowLg};
  animation: ${modalIn} ${tokens.durSlow} ${tokens.easeOut};
`;

const ModalHeader = styled.div`
  padding: 18px 22px 16px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;

    .eyebrow { ${eyebrow}; font-size: 0.58em; opacity: 0.4; }

    h3 {
      margin: 0;
      font-size: 1.12em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .status-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid transparent;
    white-space: nowrap;

    &.draft {
      background: ${({ theme }) => surface(theme, 2)};
      color: ${tokens.neutral};
      border-color: ${({ theme }) => hairline(theme, true)};
    }
    &.scheduled {
      background: ${tokens.warnSoft};
      color: ${tokens.warn};
      border-color: rgba(245, 158, 11, 0.22);
    }
    &.failed {
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border-color: rgba(239, 68, 68, 0.22);
    }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 1em; }
    &:hover:not(:disabled) { opacity: 1; border-color: ${({ theme }) => hairline(theme, true)}; }
    &:disabled { opacity: 0.25; cursor: not-allowed; }
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => hairline(theme, true)}; border-radius: 3px; }
`;

/* ---------- Seções em card ---------- */
const Section = styled.section`
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusLg};
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .section-title {
      ${eyebrow};
      font-size: 0.6em;
      opacity: 0.55;
      display: inline-flex;
      align-items: center;
      gap: 7px;

      svg { font-size: 1.15em; color: ${tokens.brand}; opacity: 0.9; }
    }

    .section-hint {
      ${mono};
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      text-align: right;
    }
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 13px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};

  svg { color: ${tokens.info}; opacity: 0.85; font-size: 0.95em; flex-shrink: 0; margin-top: 2px; }

  p { margin: 0; font-size: 0.76em; line-height: 1.55; color: ${({ theme }) => theme.text}; opacity: 0.75; letter-spacing: -0.005em; }

  &.danger {
    background: ${tokens.dangerSoft};
    border-color: rgba(239, 68, 68, 0.28);
    svg { color: ${tokens.danger}; opacity: 1; }
  }
`;

/* ---------- Campos ---------- */
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  .full { grid-column: 1 / -1; }

  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  label {
    ${mono};
    font-size: 0.62em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;

    .count {
      ${mono};
      font-size: 0.92em;
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0.01em;
      opacity: 0.75;
      white-space: nowrap;
    }
  }

  input, select {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 12px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    font-family: ${tokens.fontSans};
    font-size: 0.84em;
    outline: none;
    transition: border-color ${tokens.durBase} ${tokens.easeOut}, box-shadow ${tokens.durBase} ${tokens.easeOut};

    &:focus {
      border-color: ${tokens.brand};
      box-shadow: 0 0 0 3px ${tokens.brandRing};
    }
    &::placeholder { opacity: 0.4; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .select-wrap {
    position: relative;

    select {
      appearance: none;
      -webkit-appearance: none;
      padding-right: 34px;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    > svg {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 0.95em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      pointer-events: none;
    }
  }
`;

/* ---------- Agendamento ---------- */
const ScheduleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .schedule-current {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 13px;
    background: ${tokens.warnSoft};
    border: 1px solid rgba(245, 158, 11, 0.22);
    border-radius: ${tokens.radiusMd};
    font-size: 0.8em;
    color: ${({ theme }) => theme.text};

    strong { color: ${tokens.warn}; font-weight: 600; ${mono}; font-size: 0.95em; }
  }

  .schedule-row {
    display: flex;
    gap: 10px;
    align-items: stretch;

    .dt-field { flex: 1; min-width: 0; }
  }
`;

/* ---------- Footer / botões ---------- */
const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${tokens.brand};
  color: white;
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; box-shadow: none; }

  &.warn {
    background: ${tokens.warn};
    border-color: ${tokens.warn};
    &:hover:not(:disabled) { background: #d97706; border-color: #d97706; box-shadow: 0 4px 16px rgba(245, 158, 11, 0.25); }
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  svg { font-size: 1em; opacity: 0.8; }

  &:hover:not(:disabled) { border-color: ${tokens.brand}; color: ${tokens.brand}; background: ${tokens.brandSoft}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  &.danger-outline {
    padding: 10px 12px;
    &:hover:not(:disabled) { border-color: ${tokens.danger}; color: ${tokens.danger}; background: ${tokens.dangerSoft}; }
  }
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mesmo limite aplicado no backend (EmailMarketingController.sendTestCampaign) —
// mantido em sincronia para desabilitar o botão de teste no front antes de
// chegar a bater no bloqueio da API.
const MAX_TEST_LIST_SIZE = 25;

const STATUS_LABEL = {
  draft: "Rascunho",
  scheduled: "Agendada",
  failed: "Falhou",
};

/**
 * Converte um ISO string em valor aceito por <input type="datetime-local">
 * (respeitando o timezone local do navegador).
 */
function toLocalDateTimeInputValue(isoOrDate) {
  const d = isoOrDate ? new Date(isoOrDate) : new Date(Date.now() + 30 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Modal de gerenciamento de uma campanha em rascunho/agendada/falha
 * (diferente do `CampaignDetailDrawer`, usado só para campanhas já
 * enviadas). Permite editar todos os campos, ver o preview, enviar um
 * teste para a lista de teste, disparar de verdade para a lista real, ou
 * agendar o disparo para uma data futura.
 */
export default function CampaignManageModal({
  campaign,
  lists,
  getListContactsCount,
  onClose,
  onUpdate,
  onSendTest,
  onSend,
  onSchedule,
  onUnschedule,
  onDelete,
  fetchTemplate,
}) {
  const [name, setName] = useState(campaign?.name || "");
  const [subject, setSubject] = useState(campaign?.subject || "");
  const [senderName, setSenderName] = useState(campaign?.senderName || "FH Souza");
  const [senderEmail, setSenderEmail] = useState(campaign?.senderEmail || "");
  const [listId, setListId] = useState(campaign?.listId || lists[0]?.id || "");
  const [testListId, setTestListId] = useState(campaign?.testListId || lists[0]?.id || "");
  const [template, setTemplate] = useState(null); // { fileName, size, htmlContent }
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [scheduledAtInput, setScheduledAtInput] = useState(toLocalDateTimeInputValue(campaign?.scheduledAt));

  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  useEffect(() => {
    if (!campaign) return;
    setName(campaign.name || "");
    setSubject(campaign.subject || "");
    setSenderName(campaign.senderName || "FH Souza");
    setSenderEmail(campaign.senderEmail || "");
    setListId(campaign.listId || lists[0]?.id || "");
    setTestListId(campaign.testListId || lists[0]?.id || "");
    setScheduledAtInput(toLocalDateTimeInputValue(campaign.scheduledAt));

    if (campaign.templateFileName && fetchTemplate) {
      let cancelled = false;
      setLoadingTemplate(true);
      fetchTemplate(campaign.id)
        .then((html) => {
          if (!cancelled) setTemplate({ fileName: campaign.templateFileName, size: html?.length || 0, htmlContent: html });
        })
        .catch(() => {
          if (!cancelled) setTemplate(null);
        })
        .finally(() => {
          if (!cancelled) setLoadingTemplate(false);
        });
      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  if (!campaign) return null;

  const recipientsCount = listId ? getListContactsCount(listId) : 0;
  const testRecipientsCount = testListId ? getListContactsCount(testListId) : 0;
  const isScheduled = campaign.status === "scheduled";
  const canSave = name.trim() && subject.trim() && senderEmail.trim() && emailRegex.test(senderEmail) && listId;

  const buildPatch = () => ({
    name: name.trim(),
    subject: subject.trim(),
    senderName: senderName.trim() || "FH Souza",
    senderEmail: senderEmail.trim(),
    listId,
    testListId,
    ...(template ? { htmlContent: template.htmlContent, fileName: template.fileName } : {}),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(campaign.id, buildPatch());
      toast.success("Alterações salvas");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao salvar alterações");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testListId) {
      toast.error("Selecione uma lista de teste");
      return;
    }
    if (testRecipientsCount > MAX_TEST_LIST_SIZE) {
      toast.error(`Essa lista tem ${testRecipientsCount} contatos — grande demais para um teste. Selecione uma lista com no máximo ${MAX_TEST_LIST_SIZE}.`);
      return;
    }
    setSendingTest(true);
    try {
      await onUpdate(campaign.id, buildPatch());
      await onSendTest(campaign.id);
      toast.success(`Teste enfileirado para a lista de teste (${testRecipientsCount} contato(s))`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao enviar teste");
    } finally {
      setSendingTest(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      await onUpdate(campaign.id, buildPatch());
      await onSend(campaign.id);
      setConfirmSendOpen(false);
      toast.success("Campanha enfileirada para envio real!");
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao disparar campanha");
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledAtInput) {
      toast.error("Escolha uma data/hora para o agendamento");
      return;
    }
    const isoDate = new Date(scheduledAtInput).toISOString();
    setScheduling(true);
    try {
      await onUpdate(campaign.id, buildPatch());
      await onSchedule(campaign.id, isoDate);
      toast.success("Campanha agendada!");
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao agendar campanha");
    } finally {
      setScheduling(false);
    }
  };

  const handleUnschedule = async () => {
    setScheduling(true);
    try {
      await onUnschedule(campaign.id);
      toast.success("Agendamento cancelado — campanha voltou para rascunho");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao cancelar agendamento");
    } finally {
      setScheduling(false);
    }
  };

  const listName = lists.find((l) => l.id === listId)?.name || "—";
  const testListName = lists.find((l) => l.id === testListId)?.name || "—";
  const busy = saving || sendingTest || sending || scheduling;

  return (
    <>
      <ModalOverlay onClick={busy ? undefined : onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <div className="title-block">
              <span className="eyebrow">EMAIL MARKETING · GERENCIAR CAMPANHA</span>
              <h3 title={campaign.name}>{campaign.name}</h3>
            </div>
            <div className="header-right">
              <span className={`status-pill ${campaign.status}`}>{STATUS_LABEL[campaign.status] || campaign.status}</span>
              <button className="close-btn" onClick={onClose} disabled={busy}>
                <FiX />
              </button>
            </div>
          </ModalHeader>

          <ScrollArea>
            <Section>
              <div className="section-head">
                <span className="section-title"><FiInfo /> INFORMAÇÕES</span>
              </div>

              <FieldGrid>
                <Field className="full">
                  <label>Nome da campanha</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Newsletter Semanal" />
                </Field>

                <Field className="full">
                  <label>Assunto do email</label>
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Assunto exibido na caixa de entrada" />
                </Field>

                <Field>
                  <label>Nome do remetente</label>
                  <input type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Ex: FH Souza" />
                </Field>

                <Field>
                  <label>Email do remetente</label>
                  <input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="contato@fhsouza.com.br" />
                </Field>
              </FieldGrid>
            </Section>

            <Section>
              <div className="section-head">
                <span className="section-title"><FiUsers /> DESTINATÁRIOS</span>
                <span className="section-hint">envio real usa a lista oficial</span>
              </div>

              <FieldGrid>
                <Field>
                  <label>
                    Lista real
                    <span className="count">{recipientsCount} contato{recipientsCount !== 1 ? "s" : ""}</span>
                  </label>
                  <div className="select-wrap">
                    <select value={listId} onChange={(e) => setListId(e.target.value)}>
                      {lists.map((l) => (
                        <option key={l.id} value={l.id}>{l.name} ({getListContactsCount(l.id)})</option>
                      ))}
                    </select>
                    <FiChevronDown />
                  </div>
                </Field>

                <Field>
                  <label>
                    Lista de teste
                    <span className="count">{testRecipientsCount} contato{testRecipientsCount !== 1 ? "s" : ""}</span>
                  </label>
                  <div className="select-wrap">
                    <select value={testListId} onChange={(e) => setTestListId(e.target.value)}>
                      {lists.map((l) => (
                        <option key={l.id} value={l.id}>{l.name} ({getListContactsCount(l.id)})</option>
                      ))}
                    </select>
                    <FiChevronDown />
                  </div>
                </Field>
              </FieldGrid>

              {testRecipientsCount > MAX_TEST_LIST_SIZE && (
                <InfoBox className="danger">
                  <FiAlertTriangle />
                  <p>
                    <strong>Atenção:</strong> a lista "{testListName}" tem {testRecipientsCount} contatos — parece ser
                    uma lista real, não de teste. O envio de teste fica bloqueado para listas com mais de{" "}
                    {MAX_TEST_LIST_SIZE} contatos. Selecione uma lista pequena, só com seus emails de validação.
                  </p>
                </InfoBox>
              )}
            </Section>

            <Section>
              <div className="section-head">
                <span className="section-title"><FiFileText /> TEMPLATE</span>
                {template?.fileName && <span className="section-hint">{template.fileName}</span>}
              </div>

              {loadingTemplate ? (
                <InfoBox><FiInfo /><p>Carregando template...</p></InfoBox>
              ) : (
                <TemplateDropzone file={template} onFileAccepted={(f) => setTemplate(f)} />
              )}

              {template?.htmlContent && (
                <TemplatePreviewFrame htmlContent={template.htmlContent} height={280} />
              )}
            </Section>

            <Section>
              <div className="section-head">
                <span className="section-title"><FiClock /> AGENDAMENTO</span>
                <span className="section-hint">opcional</span>
              </div>

              <ScheduleBox>
                {isScheduled && (
                  <div className="schedule-current">
                    <span>
                      Agendada para <strong>{new Date(campaign.scheduledAt).toLocaleString("pt-BR")}</strong>
                    </span>
                  </div>
                )}
                <div className="schedule-row">
                  <Field className="dt-field">
                    <input
                      type="datetime-local"
                      value={scheduledAtInput}
                      onChange={(e) => setScheduledAtInput(e.target.value)}
                    />
                  </Field>
                  <SecondaryButton onClick={handleSchedule} disabled={busy || !canSave}>
                    <FiClock /> {scheduling ? "Agendando..." : isScheduled ? "Reagendar" : "Agendar envio"}
                  </SecondaryButton>
                </div>
                {isScheduled && (
                  <SecondaryButton onClick={handleUnschedule} disabled={busy} style={{ alignSelf: "flex-start" }}>
                    <FiXCircle /> Cancelar agendamento
                  </SecondaryButton>
                )}
              </ScheduleBox>
            </Section>
          </ScrollArea>

          <ModalFooter>
            <ButtonRow>
              <SecondaryButton onClick={handleSave} disabled={busy || !canSave}>
                <FiSave /> {saving ? "Salvando..." : "Salvar alterações"}
              </SecondaryButton>
              {onDelete && (
                <SecondaryButton
                  className="danger-outline"
                  onClick={() => onDelete(campaign)}
                  disabled={busy}
                  title="Excluir campanha"
                >
                  <FiTrash2 />
                </SecondaryButton>
              )}
            </ButtonRow>

            <ButtonRow>
              <SecondaryButton onClick={handleSendTest} disabled={busy || !testListId || testRecipientsCount > MAX_TEST_LIST_SIZE}>
                <FiZap /> {sendingTest ? "Enviando..." : `Enviar Teste${testRecipientsCount ? ` (${testRecipientsCount})` : ""}`}
              </SecondaryButton>
              <PrimaryButton
                className={campaign.status === "failed" ? "warn" : ""}
                onClick={() => setConfirmSendOpen(true)}
                disabled={busy || !canSave}
              >
                <FiSend /> {campaign.status === "failed" ? "Tentar novamente" : "Disparar Agora"}
              </PrimaryButton>
            </ButtonRow>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      <ConfirmDialog
        isOpen={confirmSendOpen}
        onClose={() => !sending && setConfirmSendOpen(false)}
        onConfirm={handleConfirmSend}
        title="Disparar campanha"
        message={`Isso enviará o email de verdade para ${recipientsCount} destinatário${recipientsCount !== 1 ? "s" : ""} da lista "${listName}". Deseja continuar?`}
        confirmText={sending ? "Enviando..." : "Disparar"}
        cancelText="Cancelar"
        type="danger"
      />
    </>
  );
}
